import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * PostPicker (Sprint BG-DataSettings 10、Phase 5、GAP A-S22)。
 *
 * 旧 `old/apps/admin/src/pages/VerificationAssignmentEditorPage.tsx` の post 一覧 +
 * チェックボックス選択 UI を v2 で再利用可能な component に切出し。AI 解析済 (status='done')
 * 投稿のみを対象に、admin が multi-select する。
 *
 * 旧との差分:
 *   - getDocs (one-shot) → onSnapshot (reactive)
 *   - props で `value: string[]` / `onChange(next)` の controlled component に統一
 *   - 検索 + 「全選択 / 全解除」 quick action を追加
 *
 * 認可: districts/{districtId}/posts は admin が rules で read 可。
 */

export interface PostPickerItem {
  id: string;
  title: string;
  authorNickname: string;
  authorUid: string;
  createdAt: Date | null;
  hasAi: boolean;
  /** 紐づくセッション (未設定の投稿は null)。REQ-4.3.20 のセッション絞り込みに使う。 */
  sessionId: string | null;
}

export interface PostPickerProps {
  districtId: string;
  /** 選択中の postId 配列 */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /**
   * REQ-4.3.20: 対象セッションで候補を絞る (空 / 未指定は絞らない)。
   * セッション未紐付けの投稿は、絞り込み時も候補に残す (セッション運用前の投稿を落とさないため。
   * 配分側の sameSessionOnly も同じ扱い)。
   */
  sessionIds?: string[];
  /** セッション ID → 表示名。行にセッション名バッジを出すために使う (任意)。 */
  sessionNameById?: Record<string, string>;
}

function mapPost(snap: { id: string; data: () => DocumentData }): PostPickerItem {
  const data = snap.data();
  const createdRaw = data.createdAt;
  const createdAt =
    createdRaw instanceof Timestamp
      ? createdRaw.toDate()
      : createdRaw && typeof (createdRaw as { toDate?: () => Date }).toDate === 'function'
        ? (createdRaw as { toDate: () => Date }).toDate()
        : null;
  const ai = data.aiAnalysis as { status?: string } | undefined;
  return {
    id: snap.id,
    title: typeof data.title === 'string' ? data.title : '',
    authorNickname: typeof data.authorNickname === 'string' ? data.authorNickname : '',
    authorUid: typeof data.authorUid === 'string' ? data.authorUid : '',
    createdAt,
    hasAi: ai?.status === 'done',
    sessionId: typeof data.sessionId === 'string' && data.sessionId ? data.sessionId : null,
  };
}

/**
 * 投稿が対象セッションの範囲に入るか (REQ-4.3.20、pure)。
 *
 * scope が空なら絞り込みなし。セッション未紐付けの投稿 (sessionId=null) は絞り込み時も残す
 * (セッション運用開始前の投稿を候補から落とさないため。配分側 sameSessionOnly と同じ扱い)。
 */
export function isPostInSessionScope(
  sessionId: string | null,
  scope: ReadonlySet<string>,
): boolean {
  if (scope.size === 0) return true;
  if (!sessionId) return true;
  return scope.has(sessionId);
}

export function PostPicker({
  districtId,
  value,
  onChange,
  disabled = false,
  sessionIds,
  sessionNameById,
}: PostPickerProps) {
  const [posts, setPosts] = useState<PostPickerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [filter, setFilter] = useState('');
  const [aiOnly, setAiOnly] = useState(true);

  useEffect(() => {
    if (!districtId) return;
    setLoading(true);
    const q = query(collection(db, 'districts', districtId, 'posts'), orderBy('createdAt', 'desc'));
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs
          .map(mapPost)
          // archivedAt 付きは除外 (v2 統一)
          .filter((p) => p.id !== '');
        setPosts(items);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [districtId]);

  const sessionScope = useMemo(() => new Set(sessionIds ?? []), [sessionIds]);

  const filtered = useMemo(() => {
    const lower = filter.trim().toLowerCase();
    return posts.filter((p) => {
      if (aiOnly && !p.hasAi) return false;
      // REQ-4.3.20: 対象セッション指定時は、そのセッションの投稿 + セッション未紐付けの投稿のみ。
      if (!isPostInSessionScope(p.sessionId, sessionScope)) return false;
      if (!lower) return true;
      return (
        p.title.toLowerCase().includes(lower) ||
        p.authorNickname.toLowerCase().includes(lower) ||
        p.id.toLowerCase().includes(lower)
      );
    });
  }, [posts, filter, aiOnly, sessionScope]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  /**
   * 選択済みのうち「対象セッション外」の投稿 (REQ-4.3.20)。
   * セッションを後から変えても選択は自動で消さず、件数と解除ボタンで気付けるようにする
   * (読込途中に選択を消してしまう事故を避ける)。
   */
  const outOfScopeSelected = useMemo(() => {
    if (sessionScope.size === 0) return [];
    return posts
      .filter((p) => selectedSet.has(p.id) && !isPostInSessionScope(p.sessionId, sessionScope))
      .map((p) => p.id);
  }, [posts, selectedSet, sessionScope]);

  function dropOutOfScope() {
    const drop = new Set(outOfScopeSelected);
    onChange(value.filter((v) => !drop.has(v)));
  }

  function toggle(id: string) {
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  }

  function selectAllFiltered() {
    const next = new Set(value);
    for (const p of filtered) next.add(p.id);
    onChange(Array.from(next));
  }

  function clearAll() {
    onChange([]);
  }

  return (
    <div className="grid grid-cols-1 gap-2" data-testid="post-picker">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="タイトル / ニックネーム / ID で絞り込み"
          aria-label="投稿フィルタ"
          disabled={disabled}
          className="flex-1 min-w-[180px] min-h-touch px-3 border border-slate-300 rounded text-sm"
        />
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={aiOnly}
            onChange={(e) => setAiOnly(e.target.checked)}
            disabled={disabled}
            aria-label="AI 解析済のみ"
          />
          <span>AI 解析済のみ</span>
        </label>
        <button
          type="button"
          onClick={selectAllFiltered}
          disabled={disabled || filtered.length === 0}
          className="text-xs text-primary-700 hover:underline disabled:opacity-40"
        >
          表示中を全選択
        </button>
        <button
          type="button"
          onClick={clearAll}
          disabled={disabled || value.length === 0}
          className="text-xs text-slate-600 hover:underline disabled:opacity-40"
        >
          全解除
        </button>
      </div>
      <p className="text-[11px] text-slate-500">
        {value.length} 件選択中 / 表示 {filtered.length} 件 / 全 {posts.length} 件
        {sessionScope.size > 0 && <>（対象セッションで絞り込み中）</>}
      </p>
      {outOfScopeSelected.length > 0 && (
        <p className="text-[11px] text-warning-700">
          選択中の {outOfScopeSelected.length} 件は対象セッション以外の投稿です。
          <button
            type="button"
            onClick={dropOutOfScope}
            disabled={disabled}
            className="ml-1 underline hover:no-underline disabled:opacity-40"
          >
            選択から外す
          </button>
        </p>
      )}
      {loading && <p className="text-xs text-slate-500">読込中…</p>}
      {error && <p className="text-xs text-danger-700">読込エラー: {error.message}</p>}
      {!loading && filtered.length === 0 && (
        <p className="text-xs text-slate-500 italic">該当投稿がありません。</p>
      )}
      {filtered.length > 0 && (
        <ul
          className="max-h-72 overflow-auto border border-slate-200 rounded bg-white"
          role="listbox"
          aria-multiselectable="true"
        >
          {filtered.map((p) => {
            const checked = selectedSet.has(p.id);
            return (
              <li key={p.id} className="border-b border-slate-100 last:border-b-0">
                <label
                  className={`flex items-start gap-2 px-2 py-1.5 cursor-pointer hover:bg-slate-50 ${
                    checked ? 'bg-primary-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(p.id)}
                    disabled={disabled}
                    aria-label={`投稿 ${p.title || p.id} を選択`}
                    className="mt-1"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-800 truncate">
                      {p.title || '(無題)'}
                      {!p.hasAi && (
                        <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-slate-200 text-slate-600">
                          AI 未解析
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-500 truncate">
                      <code>{p.id}</code> · {p.authorNickname || '(匿名)'}
                      {p.createdAt && <> · {p.createdAt.toLocaleString('ja-JP')}</>}
                      {p.sessionId ? (
                        <> · {sessionNameById?.[p.sessionId] ?? 'セッション設定あり'}</>
                      ) : (
                        <> · セッション未設定</>
                      )}
                    </p>
                  </div>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
